import { useState, useEffect } from 'react';
import { Dimensions, Platform } from 'react-native';

export interface ResponsiveBreakpoints {
  mobile: number;
  tablet: number;
  desktop: number;
}

const defaultBreakpoints: ResponsiveBreakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1200,
};

export const useResponsive = (breakpoints: ResponsiveBreakpoints = defaultBreakpoints) => {
  const [dimensions, setDimensions] = useState(() => {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        return {
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }
      // Fallback for test environment
      return {
        width: 1200,
        height: 800,
      };
    }
    const windowDimensions = Dimensions.get('window');
    // Fallback for test environment where dimensions might be undefined
    return windowDimensions || { width: 375, height: 667 };
  });

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    } else if (Platform.OS !== 'web') {
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setDimensions(window || { width: 375, height: 667 });
      });

      return () => subscription?.remove();
    }
  }, []);

  const isMobile = dimensions.width < breakpoints.mobile;
  const isTablet = dimensions.width >= breakpoints.mobile && dimensions.width < breakpoints.tablet;
  const isDesktop = dimensions.width >= breakpoints.desktop;
  const isSmallScreen = dimensions.width < breakpoints.tablet;

  return {
    dimensions,
    isMobile,
    isTablet,
    isDesktop,
    isSmallScreen,
    breakpoints,
  };
};